import { Moment } from 'moment';
import { UserExam } from '../types/User';
import { Lecture } from '../types/Lecture';
import axiosInstance from './axiosCreator';
import { store } from '../store/store';
import {
  examsFailed,
  examsLoading,
  examsReceived,
  examCreated,
  examUpdated,
} from '../slices/exams/examsSlice';
import {
  lectureDeleted,
  lectureReceived,
  lecturesFailed,
  lecturesLoading,
  lecturesReceived,
} from '../slices/lectures/lecturesSlice';
import axios from 'axios';

// Fetch exams
export const fetchExams = async () => {
  try {
    store.dispatch(examsLoading());
    const { data: response } = await axiosInstance.get('/exams');
    if (!response.success) {
      store.dispatch(examsFailed(response.error));
      throw new Error(response.error);
    }
    // Dispatch the exams to the store
    store.dispatch(examsReceived(response.data));
    return response.data;
  } catch (error) {
    console.error('Error fetching exams:', error);
    throw error;
  }
};

export const createSchoolExam = async (
  name: string,
  date: Moment | null,
  schoolId?: string
): Promise<UserExam | undefined> => {
  try {
    store.dispatch(examsLoading());
    const response = await axiosInstance.post('/exams', {
      name,
      schoolId: schoolId || null,
      date,
    });

    if (!response.data.success) {
      store.dispatch(examsFailed(response.data.error));
      throw new Error('Failed to create school exam');
    }

    store.dispatch(examCreated(response.data.data));
    return response.data.data;
  } catch (error) {
    console.error('Failed to create school exam:', error);
    return undefined;
  }
};

export const deleteCustomExam = async (examId: string) => {
  try {
    const response = await axiosInstance.delete(`/exams/${examId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting custom exam:', error);
    return {
      success: false,
      error: 'Failed to delete exam',
    };
  }
};

export interface LectureParams {
  name?: string;
  date?: Moment | null;
  vignetteIds?: string[];
  flashcardIds?: string[];
  s3Key?: string;
  enabled?: boolean;
}

export const addLectureToExam = async (
  examId: string,
  params: LectureParams
): Promise<Lecture | undefined> => {
  try {
    store.dispatch(lecturesLoading());

    const response = await axiosInstance.post<ApiResponse<Lecture>>(
      `/exams/${examId}/lectures`,
      params
    );

    if (!response.data.success || !response.data.data) {
      const errorMessage =
        response.data.error || 'Failed to add lecture to exam';
      store.dispatch(lecturesFailed(errorMessage));
      console.error('Failed to add lecture to exam:', errorMessage);
      return undefined;
    }

    // Add the new lecture to the store
    store.dispatch(lectureReceived(response.data.data));
    return response.data.data;
  } catch (error: unknown) {
    let errorMessage: string;

    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 400:
          errorMessage = 'Invalid lecture data provided';
          break;
        case 404:
          errorMessage = 'Exam not found';
          break;
        case 500:
          errorMessage = 'Internal server error while adding lecture';
          break;
        default:
          errorMessage =
            error.response?.data?.error || 'Failed to add lecture to exam';
      }
    } else {
      errorMessage = 'Unexpected error while adding lecture';
    }

    console.error(errorMessage, error);
    store.dispatch(lecturesFailed(errorMessage));
    return undefined;
  }
};

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const getExamLectures = async (
  examId: string
): Promise<Lecture[] | undefined> => {
  try {
    store.dispatch(lecturesLoading());
    const url = `/exams/${examId}/lectures`;

    const response = await axiosInstance.get<ApiResponse<Lecture[]>>(url);

    if (!response.data.success || !response.data.data) {
      const errorMessage =
        response.data.error || 'Failed to fetch exam lectures';
      store.dispatch(lecturesFailed(errorMessage));
      console.error('Failed to fetch exam lectures:', errorMessage);
      return undefined;
    }
    store.dispatch(lecturesReceived(response.data.data));
    return response.data.data;
  } catch (error: unknown) {
    console.error('Unexpected error:', error);
    return undefined;
  }
};

interface DeleteLectureResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Updates an existing lecture for a specific exam
 */
export const updateExamLecture = async (
  examId: string,
  lectureId: string,
  params: LectureParams
): Promise<Lecture | undefined> => {
  try {
    const response = await axiosInstance.put<ApiResponse<Lecture>>(
      `/exams/${examId}/lectures/${lectureId}`,
      params
    );

    if (!response.data.success || !response.data.data) {
      const errorMessage = response.data.error || 'Failed to update lecture';
      store.dispatch(lecturesFailed(errorMessage));
      console.error('Failed to update lecture:', errorMessage);
      return undefined;
    }

    // Update the lecture in the store
    store.dispatch(lectureReceived(response.data.data));
    return response.data.data;
  } catch (error: unknown) {
    let errorMessage: string;

    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 400:
          errorMessage = 'Invalid topic IDs provided';
          break;
        case 404:
          errorMessage = 'Lecture not found or does not belong to this exam';
          break;
        case 500:
          errorMessage = 'Internal server error while updating lecture';
          break;
        default:
          errorMessage =
            error.response?.data?.error || 'Failed to update lecture';
      }
    } else {
      errorMessage = 'Unexpected error while updating lecture';
    }

    console.error(errorMessage, error);
    store.dispatch(lecturesFailed(errorMessage));
    return undefined;
  }
};

/**
 * Deletes a lecture from a specific exam
 */
export const deleteExamLecture = async (
  examId: string,
  lectureId: string
): Promise<boolean> => {
  try {
    store.dispatch(lecturesLoading());

    const response = await axiosInstance.delete<DeleteLectureResponse>(
      `/exams/${examId}/lectures/${lectureId}`
    );

    if (!response.data.success) {
      const errorMessage = response.data.error || 'Failed to delete lecture';
      store.dispatch(lecturesFailed(errorMessage));
      console.error('Failed to delete lecture:', errorMessage);
      return false;
    }

    // Remove the lecture from the store
    store.dispatch(lectureDeleted(lectureId));
    return true;
  } catch (error: unknown) {
    let errorMessage: string;

    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
          errorMessage = 'Lecture not found or does not belong to this exam';
          break;
        case 500:
          errorMessage = 'Internal server error while deleting lecture';
          break;
        default:
          errorMessage =
            error.response?.data?.error || 'Failed to delete lecture';
      }
    } else {
      errorMessage = 'Unexpected error while deleting lecture';
    }

    console.error(errorMessage, error);
    store.dispatch(lecturesFailed(errorMessage));
    return false;
  }
};

export const updateExamVerified = async (examId: string, verified: boolean) => {
  const response = await axiosInstance.put(`/exams/${examId}/verify`, {
    verified,
  });

  if (!response.data.success) {
    throw new Error('Failed to set exam verified');
  }

  store.dispatch(examUpdated({ id: examId, verified }));

  return response.data.data;
};

export const selectRctExam = async (
  examId: string,
  choice: 'step1' | 'step2'
) => {
  const response = await axiosInstance.put(`/exams/${examId}/rct-select`, {
    choice,
  });

  if (!response.data.success) {
    throw new Error('Failed to select RCT exam');
  }

  return response.data;
};
